import React, { useState, useEffect, useRef } from 'react';
import { Plus, X, RefreshCw } from 'lucide-react';
import { faqService } from '../../../services/faqService';
import toast from 'react-hot-toast';

const ClinicianFaqsPage = () => {
  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Clinician FAQs';
  }, []);

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [faqData, setFaqData] = useState([]);
  const [categories, setCategories] = useState([]);
  
  const categoryDropdownRef = useRef(null);

  // Fetch FAQs from API
  const fetchFaqs = async () => {
    setLoading(true);
    try {
      const response = await faqService.getByModule('clinician');
      setFaqData(response.faqs || []);
      setCategories(response.categories || []);
    } catch (error) {
      console.error('Error fetching FAQs:', error);
      toast.error('Failed to load FAQs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFaqs();
  }, []);

  // Filter FAQs
  const filteredFaqs = faqData.filter(faq => {
    const matchesCategory = selectedCategories.length === 0 || 
      selectedCategories.includes(faq.category_name);
    const matchesSearch = !searchQuery || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  const handleViewAll = () => {
    console.log('Viewing all FAQs');
  };

  const handleAddCategory = () => {
    setShowCategoryDropdown(!showCategoryDropdown);
  };

  const handleCategorySelect = (category) => {
    if (!selectedCategories.includes(category)) {
      setSelectedCategories([...selectedCategories, category]);
    }
    setShowCategoryDropdown(false);
  };

  const handleCategoryRemove = (categoryToRemove) => {
    setSelectedCategories(selectedCategories.filter(cat => cat !== categoryToRemove));
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (categoryDropdownRef.current && !categoryDropdownRef.current.contains(event.target)) {
        setShowCategoryDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Get unique category names
  const categoryNames = categories.map(c => c.name);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="pt-2 px-6 pb-6">
        {/* First Block - Filter Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6 mb-6">
          <div className="flex gap-6">
            {/* Filter Category - 30% */}
            <div className="w-[30%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Filter Category:
              </label>
              <div className="relative" ref={categoryDropdownRef}>
                <div className="flex items-center border border-gray-300 rounded-md bg-white">
                  <div className="flex-1 px-3 py-2 min-h-[40px] flex flex-wrap gap-1 items-center">
                    {selectedCategories.length === 0 ? (
                      <span className="text-gray-400 text-sm">Please Select</span>
                    ) : (
                      selectedCategories.map((category) => (
                        <span
                          key={category}
                          className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded"
                        >
                          {category}
                          <button
                            onClick={() => handleCategoryRemove(category)}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      ))
                    )}
                  </div>
                  <button
                    onClick={handleAddCategory}
                    className="p-2 text-blue-600 hover:text-blue-800 transition-colors border-l border-gray-300"
                    title="Add Category"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                
                {/* Category Dropdown */}
                {showCategoryDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg max-h-48 overflow-y-auto">
                    {categoryNames.map((category) => (
                      <button
                        key={category}
                        onClick={() => handleCategorySelect(category)}
                        className="w-full text-left px-3 py-2 text-sm hover:bg-gray-100 focus:bg-gray-100 focus:outline-none"
                        disabled={selectedCategories.includes(category)}
                      >
                        {category}
                      </button>
                    ))}
                    {categoryNames.length === 0 && (
                      <div className="px-3 py-2 text-sm text-gray-500">No categories available</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* I want to - 50% */}
            <div className="w-[50%]">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                I want to:
              </label>
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Refresh Button */}
            <div className="flex items-end">
              <button
                onClick={fetchFaqs}
                className="p-2 text-gray-600 hover:text-blue-600 hover:bg-gray-100 rounded-lg transition-colors"
                title="Refresh FAQs"
              >
                <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              </button>
            </div>
          </div>
        </div>

        {/* Second Block - Clinician FAQs Section */}
        <div className="bg-white border border-blue-200 rounded-lg p-6">
          <h2 className="text-xl font-bold text-blue-600 mb-2">Clinician FAQs</h2>
          <div className="border-b-2 border-blue-600 mb-4"></div>
          
          {loading ? (
            <div className="text-center py-8 text-gray-500">Loading FAQs...</div>
          ) : (
            <div className="space-y-4">
              {filteredFaqs.slice(0, 10).map((faq) => (
                <div key={faq.id} className="border-b border-gray-200 pb-3 last:border-b-0">
                  <h3 className="text-gray-800 font-medium mb-2">{faq.question}</h3>
                  <p className="text-gray-600 text-sm">{faq.answer}</p>
                  {faq.category_name && (
                    <span className="inline-block mt-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-600 rounded">
                      {faq.category_name}
                    </span>
                  )}
                </div>
              ))}
              {filteredFaqs.length === 0 && (
                <p className="text-gray-500 text-center py-4">No FAQs found</p>
              )}
            </div>
          )}
          
          {filteredFaqs.length > 10 && (
            <div className="flex justify-end mt-6">
              <button
                onClick={handleViewAll}
                className="text-blue-600 hover:text-blue-800 font-medium text-sm border border-blue-600 px-4 py-2 rounded hover:bg-blue-50 transition-colors"
              >
                View All
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="bg-gray-800 h-16"></div>
    </div>
  );
};

export default ClinicianFaqsPage;
